import { SelectItem } from "../../Select/Select";
import { isNotNil } from "client/Misc/utils";
import { Mark, Schema } from "prosemirror-model";
import { Command, EditorState } from "prosemirror-state";
import { useCallback, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { addMark } from "../commands/addMark";
import { removeMark } from "../commands/removeMark";
import { FontSizeKind, FontSizeMarkAttributes } from "../schema/marks/fontSize";
import { getUnanimousValueFromSelection } from "../utils";
import {
	FontSizeName,
	fontSizeDefinition,
	supportedFontSizes
} from "client/Misc/fontTools";
import { FontSize } from "client/Api/common";
import { hasMarks } from "../schema";
import { CallbackWithSchemaResult, useExtendWithSchema } from "./useExtendWithSchema";

export type FontSizeToolHandlers = {
	isSupportedBySchema: true;
	fontSize: FontSizeValue | undefined;
	fontSizeChangeHandler(newValue: FontSizeValue): void;
	fontSizeOptions: readonly SelectItem<FontSizeValue>[];
} | {
	isSupportedBySchema: false;
};

export function useFontSizeTool(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	focus?: () => void
): FontSizeToolHandlers {
	const getValueHandler: CallbackWithSchemaResult<
		[editorState: EditorState],
		FontSizeValue | undefined
	> = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["fontSize"]),
		getFontSizeFromSelection
	);

	const changeHandler: CallbackWithSchemaResult<
		[newValue: FontSizeValue],
		void
	> = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["fontSize"]),
		useCallback(
			(
				schema: Schema<never, "fontSize">,
				newValue: FontSizeValue
			) => {
				if (newValue.kind === FontSizeKind.Default) {
					applyCommand(
						removeMark(schema.marks.fontSize)
					);
					focus?.();
				} else {
					const attrs: FontSizeMarkAttributes = newValue;

					applyCommand(
						addMark(
							schema.marks.fontSize
								.create(attrs)
						)
					);
					focus?.();
				}
			},
			[applyCommand, focus]
		)
	);

	return useMemo(
		(): FontSizeToolHandlers => {
			if (!getValueHandler.isSupportedBySchema || !changeHandler.isSupportedBySchema) {
				return {
					isSupportedBySchema: false
				};
			}

			const value: FontSizeValue | undefined = getValueHandler(editorState);

			return {
				isSupportedBySchema: true,
				fontSize: value,
				fontSizeChangeHandler: changeHandler,
				fontSizeOptions: getOptions(value)
			};
		},
		[
			changeHandler,
			editorState,
			getValueHandler
		]
	);
}

export type FontSizeValue = {
	kind: FontSizeKind.Default;
	size?: undefined;
} | {
	kind: FontSizeKind.Predefined;
	size: FontSizeName;
} | {
	kind: FontSizeKind.Custom;
	size: string;
};

const defaultFontSizeOption: FontSizeValue = {
	kind: FontSizeKind.Default
};

function createPredefinedValue(fontSize: FontSizeName): FontSizeValue {
	return {
		kind: FontSizeKind.Predefined,
		size: fontSize
	};
};

const predefinedFontSizeToValueMap: { [key in FontSizeName]: FontSizeValue } = {
	"xx-small": createPredefinedValue("xx-small"),
	"x-small": createPredefinedValue("x-small"),
	"small": createPredefinedValue("small"),
	"medium": createPredefinedValue("medium"),
	"large": createPredefinedValue("large"),
	"x-large": createPredefinedValue("x-large"),
	"xx-large": createPredefinedValue("xx-large"),
	"xxx-large": createPredefinedValue("xxx-large")
};

export function getFontSizeFromSelection(
	schema: Schema<never, "fontSize">,
	editorState: EditorState
): FontSizeValue | undefined {
	return getUnanimousValueFromSelection<FontSizeValue>(
		editorState,
		schema.marks.fontSize,
		getFontSizeValueFromMark,
		defaultFontSizeOption,
		areEqual
	);
}

function getFontSizeValueFromMark(mark: Mark): FontSizeValue {
	const attrs = mark.attrs as FontSizeMarkAttributes;
	if (attrs.kind === FontSizeKind.Custom) {
		return attrs;
	}

	return predefinedFontSizeToValueMap[attrs.size];
}

function areEqual(value1: FontSizeValue, value2: FontSizeValue): boolean {
	return value1.kind === value2.kind
		&& value1.size === value2.size;
}

function getPredefinedFontSizeLabel(name: FontSizeName): string {
	const fontSize: FontSize | undefined = supportedFontSizes
		.find(x => fontSizeDefinition[x].name === name);

	if (!fontSize) {
		throw new Error(`Font size definition was not found for the font size ${name}`);
	}

	return fontSizeDefinition[fontSize]
		.title;
}

function createPredefinedOption(fontSize: FontSizeName): SelectItem<FontSizeValue> {
	return {
		value: predefinedFontSizeToValueMap[fontSize],
		text: getPredefinedFontSizeLabel(fontSize),
		valueTrackingId: `predefined-${fontSize}`
	};
};

const fontSizePredefinedOptions: readonly SelectItem<FontSizeValue>[] = [
	{
		value: defaultFontSizeOption,
		text: `(${localize("_DefaultSize")})`,
		valueTrackingId: "default"
	},
	createPredefinedOption("xx-small"),
	createPredefinedOption("x-small"),
	createPredefinedOption("small"),
	createPredefinedOption("medium"),
	createPredefinedOption("large"),
	createPredefinedOption("x-large"),
	createPredefinedOption("xx-large"),
	createPredefinedOption("xxx-large")
];

function getOptions(value: FontSizeValue | undefined): readonly SelectItem<FontSizeValue>[] {
	const result: SelectItem<FontSizeValue>[] = [
		...fontSizePredefinedOptions
	];

	if (isNotNil(value) && value.kind === FontSizeKind.Custom) {
		result.push({
			value: value,
			text: value.size,
			valueTrackingId: `custom-${value.size}`
		});
	}

	return result;
}